/*
 *
 * AdminPage / Settings / SettingsEditor
 *
 * Edits the global `camber_terms` HTML document. Reads the raw source via the
 * authed GET /api/v3/admin/settings ({ camber_terms }) and writes via the authed
 * PUT /api/v3/admin/settings ({ setting: { camber_terms } } → { camber_terms }),
 * both verified against the rails controllers. HTML tags are preserved on both
 * read and write — no stripping occurs. Save is disabled until the text differs
 * from what's loaded, so an accidental no-op write can't happen.
 */

import React, { useEffect, useState } from "react";

import Loader from "components/Loader";
import { toast } from "sonner";
import { t } from "copy/t";
import { useAdminTerms } from "../api/queries";
import { useUpdateTerms } from "../api/mutations";
import {
  Wrapper,
  TermsTextarea,
  SaveButton,
  FormError,
  DetailLabel,
} from "./styledComponents";

const formatError = (err) => {
  if (err?.errors && typeof err.errors === "object") {
    const parts = Object.entries(err.errors).map(
      ([f, m]) => `${f} ${Array.isArray(m) ? m.join(", ") : m}`
    );
    if (parts.length) return parts.join("; ");
  }
  return err?.error || "Couldn’t save the terms. Try again.";
};

const SettingsEditor = () => {
  const { data, isLoading, isError } = useAdminTerms();
  const updateTerms = useUpdateTerms();

  const [value, setValue] = useState("");
  const [loaded, setLoaded] = useState(null); // the last-saved/loaded text
  const [error, setError] = useState(null);

  // Seed the editor once the raw terms HTML loads. GET /api/v3/admin/settings
  // returns `{ camber_terms }` — the full, un-stripped HTML source. The PUT
  // response also returns `{ camber_terms }` (handled in onSave). May be
  // null/absent for a never-set Setting → treat as empty string.
  useEffect(() => {
    if (data) {
      const html = data.camber_terms ?? "";
      setValue(html);
      setLoaded(html);
    }
  }, [data]);

  if (isLoading) {
    return (
      <Wrapper>
        <h1>Settings &amp; Terms</h1>
        <div data-testid="settings-loading">
          <Loader />
        </div>
      </Wrapper>
    );
  }

  if (isError) {
    return (
      <Wrapper>
        <h1>Settings &amp; Terms</h1>
        <p className="admin-settings__error">Couldn’t load the terms. Try again.</p>
      </Wrapper>
    );
  }

  const dirty = loaded !== null && value !== loaded;

  const onSave = () => {
    setError(null);
    updateTerms.mutate(value, {
      onSuccess: (res) => {
        const saved = res?.camber_terms ?? value;
        setLoaded(saved);
        setValue(saved);
        toast.success(t("toast.admin.termsSaved"));
      },
      onError: (err) => {
        const message = formatError(err);
        setError(message);
        toast.error(message);
      },
    });
  };

  return (
    <Wrapper>
      <h1>Settings &amp; Terms</h1>
      {error && <FormError role="alert">{error}</FormError>}

      <DetailLabel htmlFor="camber-terms">Terms (camber_terms)</DetailLabel>
      <p className="admin-settings__note">
        Edit the raw HTML source of the Terms &amp; Privacy document. Saving stores
        exactly what’s in the box — including HTML tags.
      </p>
      <TermsTextarea
        id="camber-terms"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        spellCheck={false}
      />

      <SaveButton type="button" onClick={onSave} disabled={!dirty || updateTerms.isLoading}>
        {updateTerms.isLoading ? "Saving…" : "Save terms"}
      </SaveButton>
    </Wrapper>
  );
};

export default SettingsEditor;
